import { NgModule, inject } from "@angular/core";
import { RouterModule, Routes, Router } from "@angular/router";
import { LoginComponent } from "./features/auth/login/login.component";
import { RegisterComponent } from "./features/auth/register/register.component";
import { VerifyEmailComponent } from "./features/auth/verify-email/verify-email.component";
import { ForgotPasswordComponent } from "./features/auth/forgot-password/forgot-password.component";
import { ResetPasswordComponent } from "./features/auth/reset-password/reset-password.component";
import { DashboardComponent } from "./features/dashboard/dashboard/dashboard.component";
import { AuthGuard } from "./core/guards/auth.guard";
import { RoleGuard } from "./core/guards/role.guard";
import { AuthService } from "./core/services/auth.service";

const nonAdminOnly = () => {
    const auth = inject(AuthService);
    const router = inject(Router);
    if (auth.isAdmin()) {
        router.navigate(["/dashboard/admin/entities"]);
        return false;
    }
    return true;
};

export const routes: Routes = [
    { path: "", redirectTo: "/login", pathMatch: "full" },
    { path: "login", component: LoginComponent },
    { path: "register", component: RegisterComponent },
    { path: "verify-email", component: VerifyEmailComponent },
    { path: "forgot-password", component: ForgotPasswordComponent },
    { path: "reset-password", component: ResetPasswordComponent },
    {
        path: "auth/create-entity",
        loadComponent: () =>
            import("./features/auth/create-entity/create-entity.component").then(
                (m) => m.CreateEntityComponent
            ),
        canActivate: [nonAdminOnly],
    },
    {
        path: "dashboard",
        component: DashboardComponent,
        canActivate: [AuthGuard],
        children: [
            { path: "", redirectTo: "groups", pathMatch: "full" },
            {
                path: "sites",
                loadComponent: () =>
                    import("./features/dashboard/sites/sites.component").then(
                        (m) => m.SitesComponent
                    ),
            },
            {
                path: "groups",
                loadComponent: () =>
                    import("./features/dashboard/groups/groups.component").then(
                        (m) => m.GroupsComponent
                    ),
            },
            {
                path: "users",
                loadComponent: () =>
                    import("./features/dashboard/users/users.component").then(
                        (m) => m.UsersComponent
                    ),
            },
            {
                path: "scores",
                loadComponent: () =>
                    import("./features/dashboard/scores/scores.component").then(
                        (m) => m.ScoresComponent
                    ),
            },
            {
                path: "entity",
                loadComponent: () =>
                    import("./features/dashboard/entity/entity.component").then(
                        (m) => m.EntityComponent
                    ),
            },
            {
                path: "groups/:groupId/report",
                loadComponent: () =>
                    import("./features/dashboard/groups/group-report.component").then(
                        (m) => m.GroupReportComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin", "hr_manager", "hr_personnel"] },
            },
            {
                path: "users/:userId/report",
                loadComponent: () =>
                    import("./features/dashboard/users/user-report.component").then(
                        (m) => m.UserReportComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin", "hr_manager", "hr_personnel"] },
            },
            {
                path: "campaign-wizard",
                loadComponent: () =>
                    import("./features/dashboard/campaign-wizard/campaign-wizard.component").then(
                        (m) => m.CampaignWizardComponent
                    ),
            },
            {
                path: "pending-invites",
                loadComponent: () =>
                    import("./features/admin/pending-invites/pending-invites.component").then(
                        (m) => m.PendingInvitesComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin", "hr_manager", "hr_personnel"] },
            },
            {
                path: "admin/logs",
                loadComponent: () =>
                    import("./features/admin/logs/logs.component").then(
                        (m) => m.LogsComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["super_admin"] },
            },
            {
                path: "admin/entities",
                loadComponent: () =>
                    import("./features/admin/all-entities/all-entities.component").then(
                        (m) => m.AllEntitiesComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin"] },
            },
            {
                path: "admin/entity/:entityId",
                loadComponent: () =>
                    import("./features/admin/entity-detail/entity-detail.component").then(
                        (m) => m.EntityDetailComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin"] },
            },
            {
                path: "admin/setup-workspace",
                loadComponent: () =>
                    import("./features/admin/setup-wizard/setup-wizard.component").then(
                        (m) => m.SetupWizardComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin"] },
            },
            {
                path: "admin/invite",
                loadComponent: () =>
                    import("./features/admin/invite-admin/invite-admin.component").then(
                        (m) => m.InviteAdminComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["admin", "super_admin"] },
            },
            {
                path: "admin/config",
                loadComponent: () =>
                    import("./features/admin/config/config.component").then(
                        (m) => m.ConfigComponent
                    ),
                canActivate: [RoleGuard],
                data: { roles: ["super_admin"] },
            },
        ],
    },
    { path: "**", redirectTo: "" },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule { }
